import React, { useMemo, useState } from 'react'
import { getSevColor } from '@/constants'
import type { MetricSummary, SliceMetrics } from '@/analysis/searchContract'
import SeverityHex from '@/components/table/SeverityHex'
import { explainSeverity } from '@/utils/severityExplain'
import type { DeepDiveRow } from './deepDiveRows'
import { typeTokenForStream } from '@/lib/typeId'

/**
 * Stream details over the selected range: one row per analysis window.
 * Newest (active) window stays sticky under the header; older rows scroll.
 */

type Col = {
  key: string
  label: string
  title?: string
  render?: (r: DeepDiveRow) => React.ReactNode
  value?: (r: DeepDiveRow) => { text: string; tier: 0 | 1 | 2 | null }
}

const num = (m: MetricSummary | undefined | null): number | null =>
  typeof m?.displayValue === 'number' && Number.isFinite(m.displayValue) ? m.displayValue : null

const tierOf = (m: MetricSummary | undefined | null): 0 | 1 | 2 | null =>
  m?.tier === 0 || m?.tier === 1 || m?.tier === 2 ? m.tier : null

const fmt = {
  ms: (v: number | null) => (v == null ? '—' : v < 10 ? `${v.toFixed(1)}ms` : `${Math.round(v)}ms`),
  fps: (v: number | null) => (v == null ? '—' : `${v.toFixed(1)}`),
  count: (v: number | null) => (v == null ? '—' : `${Math.round(v)}`),
  bytes: (v: number | null) =>
    v == null ? '—' : v >= 1024 ? `${(v / 1024).toFixed(1)}KB` : `${Math.round(v)}B`,
}

const pad2 = (n: number) => String(n).padStart(2, '0')
const fmtClock = (ms: number | null): string => {
  if (ms == null || !Number.isFinite(ms)) return '—'
  const d = new Date(ms)
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
}

function metricCol(
  key: string,
  label: string,
  pick: (m: SliceMetrics) => MetricSummary | undefined,
  format: (v: number | null) => string,
  title?: string,
): Col {
  return {
    key, label, title,
    value: (r) => {
      const m = r.metrics ? pick(r.metrics) : undefined
      return { text: format(num(m)), tier: tierOf(m) }
    },
  }
}

function delayIpCol(): Col {
  return {
    key: 'frameDelayIP',
    label: 'Frame delay I / P',
    title: 'I-frame and P-frame delay (aggregator proxies). B-frames are not used.',
    render: (r) => {
      const i = r.metrics?.iFrameDelay
      const p = r.metrics?.pFrameDelay
      const iT = tierOf(i)
      const pT = tierOf(p)
      return (
        <span>
          <span style={{
            color: iT == null ? 'var(--t-0)' : getSevColor(iT),
            fontWeight: iT == null ? 400 : 600,
          }}>
            {fmt.ms(num(i))}
          </span>
          <span style={{ color: 'var(--t-2)' }}> / </span>
          <span style={{
            color: pT == null ? 'var(--t-0)' : getSevColor(pT),
            fontWeight: pT == null ? 400 : 600,
          }}>
            {fmt.ms(num(p))}
          </span>
        </span>
      )
    },
  }
}

const COLS: Col[] = [
  delayIpCol(),
  metricCol('dropFrame', 'Drop frames', (m) => m.dropFrame, fmt.count),
  metricCol('packetDelay', 'Packet delay', (m) => m.packetDelay, fmt.ms),
  metricCol('jitter', 'Jitter', (m) => m.jitter, fmt.ms),
  metricCol('fps', 'FPS', (m) => m.fps, fmt.fps),
  metricCol('avgFrame', 'Avg frame', (m) => m.avgFrameBytes, fmt.bytes),
  {
    key: 'frameCountIP',
    label: 'Frames I / P',
    title: 'I-frame and P-frame counts in the window (v2 telemetry).',
    value: (r) => {
      const i = r.metrics?.iFrameCount
      const p = r.metrics?.pFrameCount
      return { text: `${fmt.count(num(i))} / ${fmt.count(num(p))}`, tier: null }
    },
  },
  metricCol('underRun', 'Buffer under', (m) => m.bufferUnderRunMs, fmt.ms),
  metricCol('overRun', 'Buffer over', (m) => m.bufferOverRun, fmt.count),
]

/** Matches sticky thead cell height so the active row pins just below it. */
const HEADER_STICKY_TOP_PX = 28

const th: React.CSSProperties = {
  position: 'sticky', top: 0, zIndex: 3,
  background: 'var(--bg-2)', color: 'var(--t-2)',
  fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em',
  textAlign: 'left', padding: '6px 8px', borderBottom: '0.5px solid var(--bd-1)',
  whiteSpace: 'nowrap', userSelect: 'none',
}
const td: React.CSSProperties = {
  padding: '5px 8px', fontSize: 11, color: 'var(--t-0)',
  borderBottom: '0.5px solid var(--bd-0)', whiteSpace: 'nowrap',
}

type SortKey = 'severity' | 'stream' | 'type' | 'clients' | 'window'

function pickActiveRow(
  rows: DeepDiveRow[],
  selectedStreamId?: string | null,
): DeepDiveRow | null {
  const pool = selectedStreamId
    ? rows.filter((r) => r.streamId === selectedStreamId)
    : rows
  if (!pool.length) return null
  return pool.reduce((best, r) =>
    Number(r.windowEndMs ?? 0) > Number(best.windowEndMs ?? 0) ? r : best,
  )
}

function RowCells({
  r,
  cellStyle,
}: {
  r: DeepDiveRow
  cellStyle: React.CSSProperties
}) {
  const typeToken = typeTokenForStream(r.streamId) ?? r.typeId ?? '—'
  const streamType = r.streamType ?? '—'
  const tip = explainSeverity({
    avgSeverity: r.severity,
    worstMetric: r.worstMetric,
    worstMetricSeverity: r.worstMetricSeverity,
    metrics: r.metrics,
  })
  const windowText = `${fmtClock(r.windowStartMs)} → ${fmtClock(r.windowEndMs)}`
  return (
    <>
      <td style={cellStyle}>
        <SeverityHex
          avg={r.severity}
          worst={r.worstMetricSeverity ?? r.severity}
          tooltip={tip}
        />
      </td>
      <td style={{ ...cellStyle, fontWeight: 600 }}>{r.streamId}</td>
      <td style={{ ...cellStyle, fontFamily: 'var(--font-mono)', fontSize: 10 }} title="Device / quality token">{typeToken}</td>
      <td style={{ ...cellStyle, fontFamily: 'var(--font-mono)', fontSize: 10 }} title="Stream protocol / transport">{streamType}</td>
      <td style={cellStyle}>{r.usersTotal ?? '—'}</td>
      <td style={{ ...cellStyle, fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--t-1)' }}>
        {windowText}
      </td>
      {COLS.map((c) => {
        if (c.render) {
          return (
            <td key={c.key} style={cellStyle} title={c.title}>
              {c.render(r)}
            </td>
          )
        }
        const v = c.value!(r)
        return (
          <td key={c.key} style={cellStyle} title={c.title}>
            <span style={{
              color: v.tier == null ? 'var(--t-0)' : getSevColor(v.tier),
              fontWeight: v.tier == null ? 400 : 600,
            }}>
              {v.text}
            </span>
          </td>
        )
      })}
    </>
  )
}

export default function DeepDiveTable({
  rows,
  selectedStreamId,
}: {
  rows: DeepDiveRow[]
  selectedStreamId?: string | null
}) {
  const [sortKey, setSortKey] = useState<SortKey>('window')
  const [asc, setAsc] = useState(false)

  const activeRow = useMemo(
    () => pickActiveRow(rows, selectedStreamId),
    [rows, selectedStreamId],
  )

  const historySorted = useMemo(() => {
    const out = rows.filter((r) => r.id !== activeRow?.id)
    out.sort((a, b) => {
      let d = 0
      if (sortKey === 'severity') d = (a.severity ?? 3) - (b.severity ?? 3)
      else if (sortKey === 'type') d = (a.typeId ?? '').localeCompare(b.typeId ?? '')
      else if (sortKey === 'clients') d = (a.usersTotal ?? -1) - (b.usersTotal ?? -1)
      else if (sortKey === 'window') {
        d = Number(a.windowEndMs ?? 0) - Number(b.windowEndMs ?? 0)
        if (d === 0) d = Number(a.windowStartMs ?? 0) - Number(b.windowStartMs ?? 0)
      } else d = a.streamId.localeCompare(b.streamId)
      if (d === 0) d = a.streamId.localeCompare(b.streamId)
      if (d === 0) d = a.id.localeCompare(b.id)
      return asc ? d : -d
    })
    return out
  }, [rows, activeRow?.id, sortKey, asc])

  const sortHeader = (key: SortKey, label: string) => (
    <th
      style={{ ...th, cursor: 'pointer' }}
      onClick={() => {
        if (sortKey === key) setAsc((v) => !v)
        else {
          setSortKey(key)
          setAsc(key !== 'window')
        }
      }}
    >
      {label}{sortKey === key ? (asc ? ' ▲' : ' ▼') : ''}
    </th>
  )

  if (!rows.length) {
    return (
      <div style={{ padding: '18px 14px', color: 'var(--t-2)', fontSize: 11, textAlign: 'center' }}>
        No analysis windows in this scope.
      </div>
    )
  }

  const activeTd: React.CSSProperties = {
    ...td,
    position: 'sticky',
    top: HEADER_STICKY_TOP_PX,
    zIndex: 2,
    background: 'var(--bg-3)',
    borderBottom: '1px solid var(--bd-2, rgba(255,255,255,0.18))',
    boxShadow: '0 1px 0 var(--bg-3)',
  }

  return (
    <div style={{ overflow: 'auto', maxHeight: 320 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {sortHeader('severity', 'Sev')}
            {sortHeader('stream', 'Stream ID')}
            {sortHeader('type', 'Type')}
            <th style={th} title="Protocol / transport (FLV, HLS, …)">Stream type</th>
            {sortHeader('clients', 'Clients')}
            {sortHeader('window', 'Window')}
            {COLS.map((c) => (
              <th key={c.key} style={th} title={c.title}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {activeRow ? (
            <tr key={`active:${activeRow.id}`} title="Latest window (pinned)">
              <RowCells r={activeRow} cellStyle={activeTd} />
            </tr>
          ) : null}
          {historySorted.map((r) => (
            <tr key={r.id}>
              <RowCells r={r} cellStyle={td} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
